import React from 'react';
import {
  FaSearch,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaTrophy,
  FaCaretDown
} from 'react-icons/fa';

/*
 * Presentational building blocks shared by the Tribe and Player leaderboards.
 * Nothing in here fetches data or owns state: the two leaderboard components
 * keep their own data logic and pass values/handlers in.
 */

// ── Formatting ─────────────────────────────────────────────
// Tribe totals arrive as strings (MySQL SUM -> DECIMAL), so coerce before
// formatting; for player rows this is a no-op.
export const fmtInt = (value) => Number(value || 0).toLocaleString();

export const fmtHours = (minutes) => (minutes ? (minutes / 60).toFixed(2) : '0.00');

// The game DB hands back UTF-8 read as Latin-1 ("FÃ¼r"); repair it for display only
// (searches and API lookups keep the stored form).
export const fixText = (s) => {
  if (!s || !/[\u00C3\u00C2][\u0080-\u00BF]/.test(s)) return s;
  try {
    const out = new TextDecoder('utf-8').decode(Uint8Array.from([...s].map((ch) => ch.charCodeAt(0) & 0xff)));
    return out.includes('\uFFFD') ? s : out;
  } catch {
    return s;
  }
};

// Names with slurs are shown as "Name hidden" (same list as mesark.net, src/lib/leaderboard.ts).
// Long stems match anywhere; short ones only as whole words, so "FRANKKK", "Nazir" and
// "raccoon" stay. Matched after undoing l33t spellings ("NlGG3R").
const BLOCKED_STEMS = ['nigg', 'niglet', 'faggot', 'fagot', 'tranny', 'retard', 'hitler', 'wetback', 'beaner', 'towelhead'];
const BLOCKED_WORDS = /^(nazi|kkk|gook|kike|chink|fag|spic|coon|rape|rapist|pedo|pedophile)s?$/;
const LEET = { 0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 7: 't', '@': 'a', $: 's', '!': 'i', '|': 'i' };

export const isBlockedName = (name) => {
  if (!name) return false;
  const base = fixText(name).normalize('NFKD').replace(/[\u0300-\u036F]/g, '').toLowerCase();
  if (/(^|\D)14\W?88(\D|$)/.test(base)) return true;
  const mapped = [...base].map((c) => LEET[c] ?? c).join('');
  return [mapped, mapped.replace(/l/g, 'i')].some((form) => {
    const plain = form.replace(/[^a-z]/g, '');
    return BLOCKED_STEMS.some((w) => plain.includes(w)) || form.split(/[^a-z]+/).some((t) => BLOCKED_WORDS.test(t));
  });
};

export const HIDDEN_NAME = 'Name hidden';

/** What to show for a player or tribe name: repaired, or hidden if it's a slur. */
export const displayName = (name) => (isBlockedName(name) ? HIDDEN_NAME : fixText(name));

export const rankTier = (rank) => {
  if (rank === 1) return 'gold';
  if (rank === 2) return 'silver';
  if (rank === 3) return 'bronze';
  return null;
};

export const getRankClass = (rank) => {
  const tier = rankTier(rank);
  return tier ? `podium rank-${tier}` : '';
};

export const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages = new Set([0, 1, current - 1, current, current + 1, total - 2, total - 1]);
  const sorted = [...pages].filter(p => p >= 0 && p < total).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(-1);
    result.push(sorted[i]);
  }
  return result;
};

// Enter / Space activate a focusable row or card like a button.
export const activateOnKey = (handler) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler();
  }
};

// ── Pieces ─────────────────────────────────────────────────
export function RankMark({ rank }) {
  const tier = rankTier(rank);
  if (!tier) return <span className="rank-num">{rank}</span>;
  return (
    <span className={`rank-badge rank-badge--${tier} clip-corner-sm`}>
      <FaTrophy className={`trophy ${tier}`} aria-hidden="true" />
      <span className="rank-badge-num">{rank}</span>
    </span>
  );
}

export function ExpandHint({ open }) {
  return <span className={`expand-hint${open ? ' is-open' : ''}`} aria-hidden="true" />;
}

export function SectionHead({ kicker, title, timeAgo }) {
  return (
    <div className="lb-head">
      <div className="lb-titleblock">
        <p className="hud-label kicker"><span className="kicker-line" />{kicker}</p>
        <h2 className="lb-title font-display">{title}</h2>
      </div>
      {timeAgo && (
        <div className="last-updated hud-label">
          <span className="live-dot" aria-hidden="true" />
          Last updated: {timeAgo}
        </div>
      )}
    </div>
  );
}

export function SearchBox({ value, placeholder, onChange, onSubmit }) {
  return (
    <div className="search-container clip-corner-sm">
      <FaSearch className="search-icon" aria-hidden="true" />
      <input
        type="text"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
        enterKeyHint="search"
        className="search-input"
      />
      <button type="button" onClick={onSubmit} className="search-button font-display">Search</button>
    </div>
  );
}

export function SelectPlate({ groupRef, label, value, open, onToggle, options, selectedKey, onSelect }) {
  return (
    <div className={`filter-group${open ? ' is-open' : ''}`} ref={groupRef}>
      <button
        type="button"
        className="filter-button clip-corner-sm"
        onClick={onToggle}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="filter-text">
          <span className="filter-label">{label}</span>
          <span className="filter-value">{value}</span>
        </span>
        <FaChevronDown className="filter-chevron" aria-hidden="true" />
      </button>
      <div className={`dropdown-content${open ? ' show' : ''}`}>
        {options.map(option => (
          <button
            type="button"
            key={option.key}
            className={`dropdown-item${option.key === selectedKey ? ' selected' : ''}`}
            aria-current={option.key === selectedKey ? 'true' : undefined}
            onClick={() => onSelect(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SortableTh({ label, active, onClick, className = '' }) {
  return (
    <th
      scope="col"
      className={`sortable-th ${className}${active ? ' is-sorted' : ''}`}
      aria-sort={active ? 'descending' : undefined}
    >
      <button type="button" className="th-sort" onClick={onClick}>
        {label}
        {active && <FaCaretDown className="sort-arrow" aria-hidden="true" />}
      </button>
    </th>
  );
}

export function ErrorState({ onRetry }) {
  return (
    <div className="error-state">
      <p className="hud-label state-kicker">{'// Error'}</p>
      <p className="state-text">Could not load leaderboard. Please try again in a few minutes.</p>
      <button type="button" className="retry-button clip-corner-sm font-display" onClick={onRetry}>Retry</button>
    </div>
  );
}

export function EmptyState({ kicker, children }) {
  return (
    <div className="empty-state">
      <p className="hud-label state-kicker">{kicker}</p>
      <p className="state-text">{children}</p>
    </div>
  );
}

export function Pagination({ currentPage, totalPages, onPageChange }) {
  if (!(totalPages > 1)) return null;
  return (
    <nav className="pagination" aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 0}
        className="pagination-button pagination-arrow clip-corner-sm"
        aria-label="Previous page"
      >
        <FaChevronLeft aria-hidden="true" />
      </button>
      {getPageNumbers(currentPage, totalPages).map((page, i) =>
        page === -1 ? (
          <span key={`ellipsis-${i}`} className="pagination-ellipsis" aria-hidden="true">...</span>
        ) : (
          <button
            type="button"
            key={page}
            onClick={() => onPageChange(page)}
            className={`pagination-button pagination-num clip-corner-sm${page === currentPage ? ' active' : ''}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page + 1}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages - 1}
        className="pagination-button pagination-arrow clip-corner-sm"
        aria-label="Next page"
      >
        <FaChevronRight aria-hidden="true" />
      </button>
      <span className="pagination-meta hud-label">Page {currentPage + 1} / {totalPages}</span>
    </nav>
  );
}
